// =============================================================================
// FILE: packages/renderer/src/components/auth/AccessRestricted.tsx
// PURPOSE: Founder's Gatekeeper — professional "Access Restricted" screen shown
//   when a blocked domain attempts to sign in. Presents a formal Application for
//   Access Restoration (Intent · Executive Review · Restoration Fee) with a
//   copy-to-clipboard block and a direct line to the founder's office.
// =============================================================================

import { useState, type JSX } from 'react';
import { ShieldAlert, Copy, Check, Mail, ArrowLeft } from 'lucide-react';

const FOUNDER_EMAIL = 'rcd2engine@gmail.com';

const APPLICATION_TEXT = `APPLICATION FOR ACCESS RESTORATION — RCD² Intelligence Engine

1. INTENT
Provide a detailed explanation of why your organization requires access to the RCD² Engine and how it will be used.

2. EXECUTIVE REVIEW
Request a formal meeting with the Founder (John Bismonte) to deliver an executive-level presentation of your organization and its intended use.

3. RESTORATION FEE
State the settlement amount your organization is prepared to provide as an access-restoration fee.

Submit this application to ${FOUNDER_EMAIL}. Access is subject to eligibility verification at the sole discretion of the RCD² administrative office.`;

export function AccessRestricted({ message, onBack }: { message: string; onBack: () => void }): JSX.Element {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(APPLICATION_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div data-testid="access-restricted" className="fixed inset-0 z-[10000] grid place-items-center p-6"
      style={{ background: 'rgba(4,7,14,0.92)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{ background: 'rgba(9,13,22,0.98)', border: '1px solid rgba(148,163,184,0.22)', boxShadow: '0 30px 120px rgba(0,0,0,0.75)', maxHeight: '92vh' }}>
        {/* Header band */}
        <div className="px-8 py-6" style={{ borderBottom: '1px solid rgba(148,163,184,0.14)', background: 'linear-gradient(180deg, rgba(30,41,59,0.4), transparent)' }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="grid place-items-center rounded-lg" style={{ width: 44, height: 44, background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.25)' }}>
              <ShieldAlert className="h-5 w-5 text-slate-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">RCD² Intelligence Engine</div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-100">Access Restricted</h2>
            </div>
          </div>
          <p className="text-[13px] text-slate-400 leading-relaxed mt-2">
            {message || 'The RCD² Engine is currently available to a select group of authorized firms and certified RCDD professionals only. Your organization is not on the current approved list.'}
          </p>
        </div>

        <div className="overflow-y-auto px-8 py-6" style={{ maxHeight: 'calc(92vh - 190px)' }}>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-3">Application for Access Restoration</div>
          <p className="text-[13px] text-slate-400 mb-4">
            Eligibility may be reviewed. To apply for restoration, submit a formal proposal to the founder's office addressing all three requirements below.
          </p>

          <ol className="space-y-3 mb-5">
            {[
              ['Intent', 'A detailed explanation of why your organization needs the RCD² Engine.'],
              ['Executive Review', 'A request to schedule a formal executive-level presentation with the Founder, John Bismonte.'],
              ['Restoration Fee', 'A proposed settlement amount your organization is willing to provide to be reinstated.'],
            ].map(([title, body], i) => (
              <li key={i} className="flex gap-3 rounded-lg p-3" style={{ background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.14)' }}>
                <span className="grid place-items-center h-6 w-6 rounded-full text-[11px] font-bold text-slate-900 shrink-0" style={{ background: '#cbd5e1' }}>{i + 1}</span>
                <div>
                  <div className="text-[13px] font-semibold text-slate-200">{title}</div>
                  <div className="text-[12px] text-slate-400">{body}</div>
                </div>
              </li>
            ))}
          </ol>

          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="copy-application-btn" onClick={copy}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-semibold tracking-wide transition"
              style={{ background: copied ? 'rgba(52,211,153,0.15)' : 'rgba(203,213,225,0.1)', border: `1px solid ${copied ? 'rgba(52,211,153,0.5)' : 'rgba(203,213,225,0.3)'}`, color: copied ? '#6ee7b7' : '#e2e8f0' }}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Application requirements copied' : 'Copy Application Requirements'}
            </button>
            <a data-testid="contact-founder-link" href={`mailto:${FOUNDER_EMAIL}?subject=Application%20for%20Access%20Restoration%20-%20RCD2%20Engine`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-semibold tracking-wide text-slate-900 transition hover:brightness-95"
              style={{ background: '#cbd5e1' }}>
              <Mail className="h-4 w-4" /> Email {FOUNDER_EMAIL}
            </a>
          </div>
        </div>

        <div className="px-8 py-4" style={{ borderTop: '1px solid rgba(148,163,184,0.14)' }}>
          <button data-testid="restricted-back-btn" onClick={onBack}
            className="inline-flex items-center gap-2 text-[12px] text-slate-400 hover:text-slate-200 transition">
            <ArrowLeft className="h-4 w-4" /> Return to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
